import type { RealtimeEventName, RealtimeEventPayloads } from '@/types';

/** Listeners are stored type-erased; `on` narrows the payload at registration. */
type ErasedListener = (payload: unknown) => void;

/**
 * Event bus standing in for the Socket.IO connection.
 *
 * Deliberately dependency-free: `realtimeService` imports it statically so
 * `on()` can stay synchronous (subscriptions are registered in the same effect
 * that opens the connection, which resolves a tick later). The scripted emitter
 * that produces the events lives in `realtime.ts` and pulls in the fixtures, so
 * it is only loaded when the mock is enabled.
 */
class MockEventBus {
  private readonly listeners = new Map<string, Set<ErasedListener>>();

  on<E extends RealtimeEventName>(event: E, listener: (payload: RealtimeEventPayloads[E]) => void): () => void {
    const erased: ErasedListener = (payload) => listener(payload as RealtimeEventPayloads[E]);
    const set = this.listeners.get(event) ?? new Set<ErasedListener>();
    set.add(erased);
    this.listeners.set(event, set);

    return () => {
      set.delete(erased);
    };
  }

  emit<E extends RealtimeEventName>(event: E, payload: RealtimeEventPayloads[E]): void {
    this.listeners.get(event)?.forEach((listener) => listener(payload));
  }

  clear(): void {
    this.listeners.clear();
  }
}

export const mockBus = new MockEventBus();
